import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Scale, ShieldAlert, Users, Megaphone, ArrowRight, ChevronRight,
  Eye, Mail, Globe, AlertTriangle, TrendingUp, Database, Network,
  Lock, CheckCircle2, Sparkles,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.16o — DARA Wagering · public category landing page.
//
// Strategic intent: position DARA as the "intelligence layer above
// the existing compliance point tools" for the entire global sports
// betting / wagering operator market. NOT a per-prospect bespoke page
// (that's /dabble) — this is the CATEGORY page that any operator
// landing via Google or referral encounters.
//
// CRITICAL: this page is NOT noindex. The bespoke pages (/dabble,
// /ventia) are noindex because they're per-prospect. This is public
// marketing — we WANT bookmakers to find it via "AU bookmaker AML
// monitoring" / "UKGC compliance intelligence" / "responsible
// gambling intervention software" search queries.
//
// Architectural pattern matches the existing industry landings:
// hero · category framing · 4-pillar framework · target operator list ·
// architecture · why DARA vs alternatives · proof points · commercial
// structures · lead form.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The 4 regulator pillars DARA covers across jurisdictions.
const PILLARS = [
  {
    Icon: Scale,
    code: "01",
    name: "AML / CTF Intelligence",
    plain: "Daily &quot;3 cases to look at before close&quot; brief for your MLRO. Pre-flight every SMR within the 24-hour AUSTRAC clock. Spot deposit-bet-withdraw cycles, structured-deposit signatures, source-of-funds anomalies. AML signal correlation with social-graph activity if you run a social product.",
    regulators: "AUSTRAC (AU) · FinCEN (US) · UKGC + FCA (UK) · MGA (Malta) · AMLD6 (EU)",
    cost_of_failure: "TaB AU · A$67M penalty (2023) · Crown Resorts · A$450M penalty · Entain UK · £17M penalty",
  },
  {
    Icon: ShieldAlert,
    code: "02",
    name: "Responsible Gambling Intervention",
    plain: "Spot customers heading toward problem gambling 30 days before crisis. Predictive harm-onset scoring. Cohort-level reporting for state regulators on demand. Cascade-aware intervention when a tipster's loss-chase pattern is spreading through followers — unique to social-product operators.",
    regulators: "NCPF (AU) · GamCare + UKGC TFC (UK) · State commissions (US) · EU TFC framework",
    cost_of_failure: "Sky Betting · £1.17M (RG) · Camelot UK · £3.15M · Multiple US state-level intervention shortfalls",
  },
  {
    Icon: Users,
    code: "03",
    name: "Integrity &amp; Match-fixing Signals",
    plain: "Detect coordinated inauthentic behaviour across the customer graph and copy-bet cascades. Suspicious correlation across new accounts. Tipster concentration risk tied to book exposure. Pre-built reporting workflow to sport-body integrity units, international integrity associations, and state-sport commissions.",
    regulators: "Sport-body integrity units · International integrity associations · State sport commissions",
    cost_of_failure: "Reputation-existential. Operator licence reviews triggered. State sport-body reviews flagged.",
  },
  {
    Icon: Megaphone,
    code: "04",
    name: "Advertising &amp; Marketing Compliance",
    plain: "Audit every customer-facing ad surface against jurisdiction-specific advertising rules. Inducement / bonus language compliance. Targeting filter compliance (BetStop-registered, self-excluded, under-21). Audit trail for every campaign creative shipped.",
    regulators: "NCPF advertising standards (AU) · CAP Code + ASA (UK) · State-by-state US · EU advertising directive",
    cost_of_failure: "AU NCPF advertising breaches escalating. UK CAP Code rulings highly visible. US state-by-state targeting fines.",
  },
];

// Named target operators across major jurisdictions. Real companies,
// public-record. Used to anchor the page's credibility — DARA knows
// who its customers are.
const TARGETS = {
  AU: ["Sportsbet (Flutter)", "Ladbrokes AU (Entain)", "Bet365 AU", "Tabcorp", "Tab.com.au", "BlueBet", "PointsBet AU", "PlayUp", "Neds", "Picklebet", "Dabble"],
  UK: ["Sky Bet", "Paddy Power · Betfair (Flutter)", "William Hill (888)", "Bet365 UK", "Entain · Ladbrokes · Coral", "BetVictor", "Sky Vegas", "LeoVegas"],
  US: ["DraftKings", "FanDuel", "BetMGM", "Caesars Sportsbook", "Hard Rock Bet", "BetRivers", "ESPN BET", "Fanatics Sportsbook"],
  // Phase 25.16u — ADJ tile obfuscated. Previously named specific
  // commercial vendors (Genius Sports / Sportradar / BPAY / Adyen
  // / Push Pay) which doubled as a free shopping list for poachers
  // wanting to know who DARA's ecosystem partners might be.
  // Replaced with categorical surface descriptions. Specific
  // vendor map provided under mutual NDA only.
  ADJ: [
    "Integrity data feeds",
    "Match-data + odds providers",
    "Payment rails + KYC processors",
    "Sport-body integrity units",
    "International integrity associations",
    "Self-exclusion + responsible-gambling registries",
  ],
};

const SOVEREIGNTY = [
  { Icon: CheckCircle2, label: "Jurisdiction-aware. AU customers run on AWS Sydney. UK on AWS London. US on AWS US-East. EU on AWS Frankfurt. Compliance posture matches operator residency.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Read-only ingest from your existing compliance stack — AML/KYC, customer-verification, compliance-data, and integrity-data layers. DARA reads, never writes. Specific integration map provided under mutual NDA.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Per-tenant database isolation. AES-256 at rest. TLS 1.3 in transit. Audit log every read. ISO 27001-aligned posture.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "Regulator-grade audit trail. Every brief, every flag, every intervention auditable end-to-end against AUSTRAC, UKGC, MGA, FinCEN, state-by-state US commissions.", colour: "text-[#00E676]" },
];

export default function Wagering() {
  usePageview("/wagering");
  // NOTE: explicitly NOT calling useNoIndex() — this is public marketing.

  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "", jurisdiction: "AU", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Phase 25.16ah — source attribution: capture ?source=why-dara (or
  // any campaign tag) from the URL so the lead lands in admin tagged
  // with where it came from. Lets the founder see which entry point
  // converts — /why-dara vs /wagering direct vs LinkedIn campaign vs
  // /dabble referral.
  const location = useLocation();
  const sourceTag = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("source") || "";
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company) {
      toast.error("Work email and operator name are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: form.company,
        role: form.role,
        message: [
          form.jurisdiction ? `Jurisdiction: ${form.jurisdiction}` : "",
          form.message ? `Notes: ${form.message}` : "",
          sourceTag ? `Source: ${sourceTag}` : "",
        ].filter(Boolean).join("\n"),
        industry: "wagering",
        source: sourceTag || "wagering-direct",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — DARA founder will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="wagering-page">
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="wagering-home-link">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · WAGERING
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/dabble/live"
              data-testid="wagering-nav-sample-brief"
              className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] hidden sm:inline-flex items-center gap-1.5"
            >
              <Eye className="w-3 h-3" /> Sample brief
            </Link>
            <a
              href="#engage"
              data-testid="wagering-nav-engage"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Talk to DARA
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-24 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4 flex items-center gap-2">
            <Globe className="w-3 h-3" />// DARA WAGERING · COMPLIANCE INTELLIGENCE FOR SPORTS BETTING OPERATORS
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            Sports betting compliance, <span className="text-[#FFB000]">monitored end-to-end</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            The regulator-grade intelligence layer for AU, UK, EU, and US sports betting operators. AML / CTF · responsible gambling · integrity &amp; match-fixing · advertising compliance. <b>One daily brief, one audit trail, every morning.</b>
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            We don't replace your existing compliance stack. We make it work together — so your compliance lead walks into the day knowing exactly which 3 cases need attention, exactly where every regulator surface sits, and exactly what to do today. <b>Specific architecture detail and vendor mapping is shared under mutual NDA.</b>
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="wagering-hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Talk to DARA's founder <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/dabble/live"
              data-testid="wagering-hero-sample-cta"
              className="inline-flex items-center gap-2 border border-[#FFB000] text-[#FFB000] font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-[#FFB000]/10"
            >
              <Eye className="w-4 h-4" /> See a sample morning brief
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORY URGENCY */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />// THE REGULATOR SURFACE IS HARDENING · GLOBALLY
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Every sports betting operator is now responsible for what their <span className="text-[#FFB000]">customers do</span>. DARA gives you the audit trail.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            <UrgencyCard
              jurisdiction="AUSTRALIA"
              regulator="AUSTRAC · NCPF · ACMA · State commissions"
              event="TaB AU · A$67M AUSTRAC penalty (2023). Crown Resorts · A$450M civil penalty. NCPF v2 in force. BetStop register mandatory."
            />
            <UrgencyCard
              jurisdiction="UNITED KINGDOM"
              regulator="UKGC · FCA · CAP / ASA"
              event="Entain · £17M penalty. Sky Betting · £1.17M (RG). Camelot · £3.15M. UKGC enforcement budget +40% in 2025."
              accent
            />
            <UrgencyCard
              jurisdiction="UNITED STATES"
              regulator="State commissions · FinCEN · UIGEA"
              event="State-by-state RG enforcement scaling fast. AGA expanding voluntary best-practice into mandatory in 3 states. Tribal-state revenue audits hardening."
            />
          </div>
        </div>
      </section>

      {/* 4-PILLAR FRAMEWORK */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// THE 4-PILLAR FRAMEWORK</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            What DARA actually does &nbsp;<span className="text-zinc-500 font-mono text-base font-normal">— in plain English</span>
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            One daily brief covering four regulator pillars, each one mapped to your jurisdiction's specific regulators and historical enforcement actions. Same engine, different language per pillar.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="wagering-pillars-grid">
            {PILLARS.map(({ Icon, code, name, plain, regulators, cost_of_failure }) => (
              <div key={code} className="bg-black p-6 sm:p-7 flex flex-col" data-testid={`wagering-pillar-${code}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-5 h-5 text-[#FFB000]" />
                  <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">// PILLAR {code}</span>
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight leading-tight">{name}</h3>
                <div className="mt-4 border-l-2 border-[#FFB000] pl-3 py-1 bg-[#FFB000]/5">
                  <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">▸ IN PLAIN ENGLISH</div>
                  <p className="text-sm text-zinc-100 leading-relaxed">{plain}</p>
                </div>
                <div className="mt-4 space-y-3 flex-1">
                  <div>
                    <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-[#00E676] mb-1">▸ REGULATORS COVERED</div>
                    <p className="text-xs text-zinc-300 leading-relaxed">{regulators}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FF6B47] mb-1">▸ COST OF FAILURE · REAL EXAMPLES</div>
                    <p className="text-xs text-zinc-400 leading-relaxed">{cost_of_failure}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO DARA SERVES */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// WHO DARA WAGERING IS BUILT FOR</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Any operator carrying a wagering licence.</h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            Mid-tier or enterprise. Australian-owned or global. Sports-only or multi-product. The shape of the compliance problem is identical — only the regulators change.
          </p>

          {/* Phase 25.16aa — Operator Privacy Posture trust strip.
              Sits between the section description and the operator grid
              so a competitor's eye hits the per-operator isolation
              guarantee BEFORE seeing the highlighted "IN CONVERSATION"
              line on Dabble. Defuses the "DARA will share our data with
              the operator they highlighted" fear at the exact scan
              moment it could be triggered. */}
          <div
            className="mt-8 border border-[#FFB000]/30 bg-[#FFB000]/5 p-4 sm:p-5 flex items-start gap-3"
            data-testid="wagering-operator-privacy-posture"
          >
            <Lock className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-1.5">
                // OPERATOR PRIVACY POSTURE · ENGINEERED · NOT PROMISED
              </div>
              <p className="text-xs sm:text-sm text-zinc-200 leading-relaxed">
                Per-operator tenant database · per-operator AES-256 encryption key · zero cross-tenant queries · every read audit-logged. Every operator on this page runs in isolation. <span className="text-[#FFB000] font-bold">No operator's data, signals, customer graph, or playbook is ever visible to another operator</span> — guaranteed at the database layer, not by policy.
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <TargetGroup label="AUSTRALIA" flag="🇦🇺" items={TARGETS.AU} highlight="Dabble" />
            <TargetGroup label="UNITED KINGDOM" flag="🇬🇧" items={TARGETS.UK} />
            <TargetGroup label="UNITED STATES" flag="🇺🇸" items={TARGETS.US} />
            <TargetGroup label="ADJACENT SURFACES" flag="🌐" items={TARGETS.ADJ} />
          </div>

          <p className="mt-8 text-xs text-zinc-500 max-w-3xl leading-relaxed">
            Operators listed are public-record industry participants in the global wagering market and represent the addressable market for DARA Wagering. No statement of commercial relationship is implied except where explicitly highlighted.
          </p>
        </div>
      </section>

      {/* ARCHITECTURE / TRUST */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// HOW IT WORKS · WHY THE CTO &amp; CCO BOTH NOD</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl">
            <span className="text-[#FFB000]">Read-only</span>. Jurisdiction-aware. Tenant-isolated. Regulator-auditable.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {SOVEREIGNTY.map(({ Icon, label, colour }, i) => (
              <div key={i} className="flex items-start gap-3 bg-black border border-zinc-900 p-5 sm:p-6">
                <Icon className={`w-5 h-5 ${colour} flex-shrink-0 mt-0.5`} />
                <p className="text-sm text-zinc-200 leading-relaxed">{label}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-zinc-900 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <ArchBlock
              Icon={Database}
              label="INGEST"
              body="Your existing operating stack — odds &amp; risk engines · AML/KYC layer · compliance-data &amp; investigation layer · customer-verification layer · social graph · app perf monitor · self-exclusion register · payment processors · marketing platform · integrity-data providers. Specific connector map under NDA."
            />
            <ArchBlock
              Icon={Network}
              label="DARA WAGERING"
              body="Reconciliation across feeds · jurisdiction-aware anomaly classifier · bespoke regulator-language narrator · daily compliance brief · per-pillar regulator pack template · audit log every read"
              accent
            />
            <ArchBlock
              Icon={Lock}
              label="DELIVERY"
              body="Morning brief to CCO + MLRO + Trading + Growth · responsible-gambling intervention queue · integrity-body pre-formatted reports · AUSTRAC / UKGC / MGA / state pre-flight reports · on-demand audit pack"
            />
          </div>
        </div>
      </section>

      {/* WHY NOT THE ALTERNATIVES */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// WHY DARA · NOT THE ALTERNATIVES</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            We're <span className="text-[#FFB000]">not</span> the compliance tool. We're the layer <span className="text-[#FFB000]">above</span> it.
          </h2>

          <div className="mt-10 overflow-hidden border border-zinc-900">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900">
                <tr>
                  <th className="text-left p-3 font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">CATEGORY</th>
                  <th className="text-left p-3 font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">WHAT THEY DO</th>
                  <th className="text-left p-3 font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">DARA RELATIONSHIP</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {[
                  ["AML / KYC point-tool vendors", "Per-customer screening", "DARA reads their output; never replaces"],
                  ["Compliance data + investigation-graph vendors", "Sanctions / PEP / behavioural data", "DARA orchestrates with them"],
                  ["Customer-verification vendors", "Onboarding identity checks", "DARA reads their flags; no overlap"],
                  ["Integrity-data providers", "Match-fixing / suspicious-betting feeds", "DARA ingests their signals into the brief"],
                  ["Internal builds (in-house ops tools)", "Per-operator bespoke compliance reporting", "DARA replaces the internal-build burden"],
                  ["DARA Wagering", "Daily brief + audit trail across the stack you already have", "A different layer entirely"],
                ].map(([player, what, rel], i) => (
                  <tr key={i} className={`border-t border-zinc-900 ${i === 5 ? "bg-[#FFB000]/5" : ""}`}>
                    <td className={`p-3 ${i === 5 ? "text-[#FFB000] font-bold" : "text-white font-bold"}`}>{player}</td>
                    <td className="p-3 text-zinc-400">{what}</td>
                    <td className={`p-3 ${i === 5 ? "text-[#FFB000] font-bold" : "text-zinc-300"}`}>{rel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phase 25.16t — NDA-gated detail line. Replaces the
              specific-vendor-naming that was previously a free shopping
              list for poachers. Signals depth-under-NDA, filters
              tire-kickers, creates the "ask us under NDA" hook. */}
          <p className="mt-5 text-xs sm:text-sm text-zinc-500 max-w-3xl leading-relaxed italic">
            Specific vendor-to-DARA integration mapping, architecture detail, and the named-vendor competitive landscape are <span className="text-[#FFB000] not-italic font-bold">provided under mutual NDA</span> to qualified operators only. Talk to DARA's founder via the form below.
          </p>

          {/* Phase 25.16ae — preempt the "we already use Claude / our
              engineers can build this" objection right after the
              alternatives table. Discreet link, not a CTA — for the
              prospect who reads the table and immediately thinks "this
              is just a Claude wrapper, we can build it." */}
          <div className="mt-6 border-t border-zinc-900 pt-5 flex items-start gap-3" data-testid="wagering-why-dara-link-row">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed">
              // OR · ALREADY USING AN LLM INTERNALLY?
            </div>
            <Link
              to="/why-dara"
              data-testid="wagering-why-dara-link"
              className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] hover:underline inline-flex items-center gap-1"
            >
              See the 7 layers DARA ships above the model <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// COMMERCIAL · FOUR ENTRY POINTS</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Four ways to start. Pick the entry that matches your procurement cycle.</h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            Most operators start at the <span className="text-[#FFB000] font-bold">Paid Proof-of-Concept</span> tier — small enough to clear procurement quickly, structured enough to prove ROI, with fees credited toward annual contracts on conversion.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            <DealCard
              tag="LIGHTHOUSE PILOT"
              price="By invitation · 12 weeks · 1 pillar"
              body="By invitation · 1-2 slots per year. DARA picks up the cost in exchange for a published case study + 2 reference calls + first-look on expansion. Best for category-defining design partners. Most operators enter at the Paid POC tier."
            />
            <DealCard
              tag="PAID PROOF-OF-CONCEPT"
              price="Contact for pricing · 6-8 weeks · 1 pillar"
              body="Production-grade integration on one pillar with measurable success criteria. Fee credits 100% toward annual contract if you convert. Faster procurement than free pilots — most operators clear this in 2-3 weeks. Standard MNDA."
              highlight
            />
            <DealCard
              tag="STANDARD"
              price="Contact for pricing · per pillar · annual"
              body="Production deployment across one regulator pillar (e.g. AML/CTF intelligence OR responsible-gambling intervention) with quarterly reviews. Standard MNDA + SaaS terms. Natural step up from a converted POC."
            />
            <DealCard
              tag="OPERATING PARTNER"
              price="Contact for pricing · all 4 pillars · 24-mo"
              body="Full DARA Wagering deployment across all 4 regulator pillars. Dedicated DARA engineer embedded with your compliance + trading teams. Quarterly co-developed roadmap. Source-available licence."
            />
          </div>
        </div>
      </section>

      {/* ENGAGE FORM */}
      <section id="engage" className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// CONTINUE THE CONVERSATION</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Talk to DARA's founder · 30 minutes.</h2>
          <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
            Submit details and DARA's founder will reach out within one business day to schedule a 30-minute discovery. Not a procurement engagement — a working conversation about your specific regulator surface. NDA available pre-call if needed.
          </p>

          {submitted ? (
            <div className="mt-8 border border-[#00E676]/30 bg-[#00E676]/5 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#00E676]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[#00E676]">RECEIVED</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Thanks — DARA's founder will reach out within one business day. In the meantime, see a sample morning brief at <Link to="/dabble/live" className="text-[#FFB000] underline-offset-2 hover:underline">daraos.ai/dabble/live</Link>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5 border border-zinc-900 bg-black p-6 sm:p-8" data-testid="wagering-engage-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label="WORK EMAIL *" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} type="email" required testid="wagering-form-email" />
                <Input label="OPERATOR / COMPANY *" value={form.company} onChange={(v) => setForm(f => ({ ...f, company: v }))} required testid="wagering-form-company" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label="FULL NAME" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="wagering-form-name" />
                <Input label="ROLE / TITLE" value={form.role} onChange={(v) => setForm(f => ({ ...f, role: v }))} testid="wagering-form-role" />
              </div>
              <label className="block">
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">PRIMARY JURISDICTION</span>
                <select
                  value={form.jurisdiction}
                  onChange={(e) => setForm(f => ({ ...f, jurisdiction: e.target.value }))}
                  data-testid="wagering-form-jurisdiction"
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                >
                  <option value="AU">Australia (AUSTRAC · NCPF · ACMA · State)</option>
                  <option value="UK">United Kingdom (UKGC · FCA · CAP)</option>
                  <option value="US">United States (State commissions · FinCEN)</option>
                  <option value="EU">EU (MGA Malta · AMLD6)</option>
                  <option value="NZ">New Zealand</option>
                  <option value="CA">Canada</option>
                  <option value="OTHER">Other / multi-jurisdiction</option>
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">ANYTHING SPECIFIC YOU WANT TO TABLE (OPTIONAL)</span>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="wagering-form-message"
                  rows={4}
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </label>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-[11px] sm:text-[10px] text-zinc-600 font-mono leading-relaxed">
                  By submitting you accept that DARA will contact you about this<br />opportunity. Mutual NDA available pre-pilot.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="wagering-form-submit"
                  className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  {submitting ? "Sending..." : "Send to DARA founder"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA Wagering</div>
            <ul className="space-y-2">
              <li><Link to="/dabble/live" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Sample morning brief</Link></li>
              <li><Link to="/regulator-pack-preview" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Regulator pack preview</Link></li>
              <li><Link to="/industrial-demo?profile=sportsbook" className="text-xs sm:text-[11px] text-zinc-500 hover:text-[#FFB000]">Engineering view · live telemetry</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <ul className="space-y-2">
              <li><Link to="/" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Home</Link></li>
              <li><Link to="/security" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Security &amp; Trust</Link></li>
              <li><Link to="/industries/mining" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Other verticals · industrial</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Contact</div>
            <p className="text-xs sm:text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed mt-2 font-mono">
              majisonmanuel@gmail.com
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-700">DARA WAGERING · v1</span>
        </div>
      </footer>
    </div>
  );
}

function UrgencyCard({ jurisdiction, regulator, event, accent = false }) {
  return (
    <div className={`bg-black p-6 ${accent ? "border-l-2 border-[#FFB000]" : ""}`}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// {jurisdiction}</div>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">{regulator}</div>
      <p className="text-sm text-zinc-200 leading-relaxed">{event}</p>
    </div>
  );
}

function TargetGroup({ label, flag, items, highlight }) {
  return (
    <div className="bg-black border border-zinc-900 p-5">
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
        <span>{flag}</span>{label}
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => {
          const isHighlight = highlight && item.includes(highlight);
          return (
            <li
              key={i}
              className={`text-xs leading-relaxed ${isHighlight ? "text-[#00E676] font-bold" : "text-zinc-400"}`}
            >
              {isHighlight ? "▸ " : "· "}{item}
              {isHighlight && <span className="font-mono text-[10px] sm:text-[9px] ml-2 text-[#00E676]">· IN CONVERSATION</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ArchBlock({ Icon, label, body, accent = false }) {
  return (
    <div className={`p-5 ${accent ? "border border-[#FFB000]/40 bg-[#FFB000]/5" : "border border-zinc-900"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent ? "text-[#FFB000]" : "text-zinc-400"}`} />
        <span className={`font-mono text-[11px] sm:text-[10px] uppercase tracking-widest ${accent ? "text-[#FFB000]" : "text-zinc-500"}`}>// {label}</span>
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function DealCard({ tag, price, body, highlight = false }) {
  return (
    <div className={`p-6 ${highlight ? "bg-[#FFB000]/10 border-l-2 border-[#FFB000]" : "bg-black"}`}>
      <div className={`font-mono text-[11px] sm:text-[10px] uppercase tracking-widest ${highlight ? "text-[#FFB000]" : "text-zinc-500"} mb-2`}>// {tag}</div>
      <div className="font-heading font-bold text-lg tracking-tight mb-3">{price}</div>
      <p className="text-xs text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
